import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
  random,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {GradientText} from "../components/dimus/GradientText";
import {GlowText} from "../components/dimus/GlowText";
import {SceneTransition} from "../components/dimus/SceneTransition";
import {Counter} from "../components/dimus/Counter";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * PERGUNTAS QUE VOCÊ DEVERIA ESTAR FAZENDO #04
 * "Quanto tempo seu lead espera até ser atendido?"
 * Editing Style: CINEMATIC — Blue palette
 * Evolução: Countdown 24h, barra de conversão, dado "21x"
 */

const BLUE = "#60a5fa";
const BLUE_DARK = "#050f1a";
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";
const RED = "#F87171";
const AMBER = "#f5ad43";
const GREEN = "#00DF82";

const SZ = SAFE_ZONES.universal;

const SCENES = [
  {dur: 104, file: "audio/perguntas04/perguntas04_c01.mp3", dir: "right" as const},
  {dur: 387, file: "audio/perguntas04/perguntas04_c02.mp3", dir: "left" as const},
  {dur: 750, file: "audio/perguntas04/perguntas04_c03.mp3", dir: "up" as const},
  {dur: 252, file: "audio/perguntas04/perguntas04_c04.mp3", dir: "right" as const},
  {dur: 193, file: "audio/perguntas04/perguntas04_c05.mp3", dir: "up" as const},
];

export const PERGUNTAS04_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);

const Particles: React.FC<{count: number; color: string}> = ({count, color}) => {
  const frame = useCurrentFrame();
  return (
    <>
      {Array.from({length: count}, (_, i) => {
        const x = random(`ppx4-${i}`) * 1080;
        const baseY = random(`ppy4-${i}`) * 1920;
        const size = 2 + random(`pps4-${i}`) * 3;
        const speed = 0.2 + random(`ppsp4-${i}`) * 0.35;
        const wrapped = ((baseY - frame * speed) % 2100 + 2100) % 2100 - 100;
        return (
          <div key={i} style={{
            position: "absolute", left: x, top: wrapped,
            width: size, height: size, borderRadius: "50%",
            background: color, opacity: 0.07 + random(`ppo4-${i}`) * 0.1,
          }} />
        );
      })}
    </>
  );
};

// ─── Conversion Bar ──────────────────────────────────────────────────
const ConvBar: React.FC<{
  label: string; pct: number; color: string; delay: number;
}> = ({label, pct, color, delay}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.smooth});
  if (frame < delay) return null;
  return (
    <div style={{opacity: p, width: "85%", display: "flex", flexDirection: "column", gap: 6}}>
      <div style={{display: "flex", justifyContent: "space-between"}}>
        <span style={{fontSize: FONT_SCALE.label.min, fontWeight: 600, color: WHITE, fontFamily: FONT_STACK}}>{label}</span>
        <span style={{fontSize: FONT_SCALE.label.min, fontWeight: 800, color, fontFamily: FONT_STACK}}>{pct}%</span>
      </div>
      <div style={{width: "100%", height: 12, borderRadius: 6, background: "rgba(255,255,255,0.06)"}}>
        <div style={{
          width: `${pct * p}%`, height: "100%", borderRadius: 6,
          background: `linear-gradient(90deg, ${color}cc, ${color})`,
          boxShadow: `0 0 12px ${color}44`,
        }} />
      </div>
    </div>
  );
};

// ─── SCENE 1: HOOK ────────────────────────────────────────────────────
const S1: React.FC = () => (
  <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
    <Audio src={staticFile(SCENES[0].file)} volume={0.9} />
    <GradientText text="PERGUNTAS #04" colors={[BLUE, AMBER]} fontSize={FONT_SCALE.h1.max} springProfile="bouncy" />
  </AbsoluteFill>
);

// ─── SCENE 2: O DADO — 24h counter ───────────────────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const textP = spring({frame: frame - 120, fps, config: SPRINGS.smooth});

  return (
    <OrganicLayer intensity="subtle" seed="perguntas-04">
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[1].file)} volume={0.9} />
      <div style={{fontSize: FONT_SCALE.label.max, fontWeight: 600, color: MUTED, fontFamily: FONT_STACK,
        opacity: spring({frame: frame - 5, fps, config: SPRINGS.smooth}),
      }}>
        TEMPO MÉDIO DE RESPOSTA
      </div>
      <Counter value={24} suffix="h" fontSize={160} color={RED} delay={20} springProfile="heavy" />
      <div style={{
        opacity: textP, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3, maxWidth: "85%",
      }}>
        Nesse tempo o lead já pesquisou,{"\n"}já pediu orçamento, já esqueceu de você.
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 3: O IMPACTO — barras de conversão ────────────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  // Fase A (0-300): Barras de conversão
  const showBars = frame < 320;
  // Fase B (300-500): "21x mais chance"
  const showStat = frame >= 300 && frame < 520;
  const statP = spring({frame: frame - 310, fps, config: SPRINGS.heavy});
  // Fase C (500+): "O primeiro que responde leva"
  const showFinal = frame >= 500;

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 16}}>
      <Audio src={staticFile(SCENES[2].file)} volume={0.9} />

      {showBars && (
        <div style={{display: "flex", flexDirection: "column", gap: 14, alignItems: "center", width: "100%"}}>
          <div style={{fontSize: FONT_SCALE.label.max, fontWeight: 600, color: MUTED, fontFamily: FONT_STACK,
            opacity: spring({frame: frame - 5, fps, config: SPRINGS.smooth}),
          }}>
            CHANCE DE CONVERSÃO
          </div>
          <ConvBar label="5 min" pct={90} color={GREEN} delay={20} />
          <ConvBar label="30 min" pct={45} color={AMBER} delay={50} />
          <ConvBar label="1 hora" pct={20} color={RED} delay={80} />
          <ConvBar label="24 horas" pct={5} color={RED} delay={110} />
        </div>
      )}

      {showStat && (
        <div style={{opacity: statP, textAlign: "center"}}>
          <GradientText text="21×" colors={[GREEN, BLUE]} fontSize={140} springProfile="heavy" />
          <div style={{
            opacity: spring({frame: frame - 370, fps, config: SPRINGS.smooth}),
            fontSize: FONT_SCALE.h2.min, fontWeight: 600, color: WHITE,
            fontFamily: FONT_STACK, marginTop: 8,
          }}>
            mais chance em 5 min{"\n"}vs 30 min
          </div>
        </div>
      )}

      {showFinal && (
        <GlowText
          text="O primeiro que responde leva."
          delay={510}
          size={FONT_SCALE.h2.max}
          color={BLUE}
        />
      )}
    </AbsoluteFill>
  );
};

// ─── SCENE 4: A PERGUNTA REAL ─────────────────────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 10, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 80, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
      <Audio src={staticFile(SCENES[3].file)} volume={0.9} />
      <div style={{
        opacity: p1, transform: `translateY(${interpolate(p1, [0, 1], [20, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED,
        fontFamily: FONT_STACK, textAlign: "center",
      }}>
        A pergunta certa não é:
      </div>
      <div style={{
        opacity: p1, fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE,
        fontFamily: FONT_STACK, textAlign: "center", textDecoration: "line-through", textDecorationColor: RED,
      }}>
        "Quanto investir em tráfego?"
      </div>
      <div style={{
        opacity: p2, transform: `scale(${interpolate(p2, [0, 1], [0.9, 1], CLAMP)})`,
        fontSize: FONT_SCALE.h2.min, fontWeight: 800, color: BLUE,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.2,
      }}>
        "Quanto tempo meu{"\n"}lead espera?"
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: CTA ────────────────────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const textP = spring({frame: frame - 8, fps, config: SPRINGS.heavy});
  const profileP = spring({frame: frame - 60, fps, config: SPRINGS.snappy});
  const btnP = spring({frame: frame - 100, fps, config: SPRINGS.bouncy});
  const pulse = frame > 120 ? 1 + Math.sin((frame - 120) * 0.08) * 0.02 : 1;

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", gap: 20, flexDirection: "column", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(SCENES[4].file)} volume={0.9} />
      <div style={{
        opacity: textP, transform: `translateY(${interpolate(textP, [0, 1], [15, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.body.min, fontWeight: 600, color: WHITE,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3, maxWidth: "88%",
      }}>
        Manda um lead teste.{"\n"}Conta quanto tempo demora.
      </div>
      <div style={{
        opacity: profileP, display: "flex", alignItems: "center", gap: 16,
        transform: `translateY(${interpolate(profileP, [0, 1], [20, 0], CLAMP)}px)`,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", overflow: "hidden",
          border: `3px solid ${BLUE}`, boxShadow: `0 0 20px ${BLUE}30`,
        }}>
          <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
            style={{width: "100%", height: "100%", objectFit: "cover"}} />
        </div>
        <div>
          <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>@guilhermeribeiro.ai</div>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>Perguntas que importam</div>
        </div>
      </div>
      <div style={{
        opacity: btnP,
        transform: `translateY(${interpolate(btnP, [0, 1], [30, 0], CLAMP)}px) scale(${interpolate(btnP, [0, 1], [0.5, 1], CLAMP) * pulse})`,
        padding: "18px 52px", borderRadius: 999, background: BLUE,
        fontSize: FONT_SCALE.cta.max, fontWeight: 700, color: "#000", fontFamily: FONT_STACK,
        boxShadow: `0 0 40px ${BLUE}40`,
      }}>
        SEGUE PRA MAIS
      </div>
    </AbsoluteFill>
  );
};

const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 8], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 10, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

export const Perguntas04: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 35%, ${BLUE_DARK}, #000 70%)`}} />
      <Noise opacity={0.02} />
      <Particles count={25} color={BLUE} />
      <FilmGrain opacity={0.04} />
      <Vignette intensity={0.7} />

      {SCENES.map(({dur, dir}, i) => {
        const from = offset;
        offset += dur;
        const Comp = [S1, S2, S3, S4, S5][i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <SceneTransition type="in" direction={dir} springProfile="smooth">
                <Comp />
              </SceneTransition>
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
